import { useEffect, useState } from "react";
import api from "@/lib/api";
import { CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

const fmtUptime = (s) => {
  if (!s || s < 0) return "—";
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};

export default function HealthPill() {
  const { t } = useTranslation();
  const [h, setH] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const { data } = await api.get("/admin/health");
        if (!alive) return;
        setH(data);
        setError(false);
      } catch {
        if (!alive) return;
        setError(true);
      }
    };
    tick();
    const id = setInterval(tick, 30000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  if (error) {
    return (
      <span
        className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#FFE6E0] text-[#D14848] border border-[#D14848]/20 text-[11px] font-display font-bold"
        data-testid="health-pill-error"
      >
        <AlertTriangle className="w-3 h-3" strokeWidth={3}/> {t("health.offline")}
      </span>
    );
  }
  if (!h) {
    return (
      <span
        className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#F4F2ED] text-[#7C828D] text-[11px] font-display font-bold"
        data-testid="health-pill-loading"
      >
        <Loader2 className="w-3 h-3 animate-spin" strokeWidth={3}/> {t("health.checking")}
      </span>
    );
  }
  const ok = h.ok && h.db_ok;
  return (
    <span
      title={`Boot ${new Date(h.boot_at).toLocaleString()} · ${h.user_count} users · ${h.pending_id_reviews} pending reviews${h.last_error_at ? " · last error " + new Date(h.last_error_at).toLocaleString() : ""}`}
      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-display font-bold border ${
        ok
          ? "bg-[#DDF5EA] text-[#0F9D8F] border-[#14C8B0]/30"
          : "bg-[#FFE6E0] text-[#D14848] border-[#D14848]/20"
      }`}
      data-testid={ok ? "health-pill-ok" : "health-pill-bad"}
    >
      <span className={`relative flex h-2 w-2`}>
        <span className={`absolute inline-flex h-full w-full rounded-full opacity-70 ${ok ? "bg-[#14C8B0] animate-ping" : "bg-[#D14848]"}`}></span>
        <span className={`relative inline-flex rounded-full h-2 w-2 ${ok ? "bg-[#14C8B0]" : "bg-[#D14848]"}`}></span>
      </span>
      {ok ? t("health.online") : t("health.issue")}
      <span className="opacity-60 ml-1 font-semibold">· {fmtUptime(h.uptime_seconds)}</span>
    </span>
  );
}
